import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { offlineQueueStore, QueuedAction } from '@/lib/offlineQueueStore';

const MAX_RETRIES = 3;

// Migration: remove old localStorage queue if it exists
const LEGACY_QUEUE_KEY = 'lupuslogix_offline_queue';
type SyncStatus = 'syncing' | 'complete' | 'offline' | 'online';
type DynamicTableClient = {
  from: (table: string) => {
    insert: (data: Record<string, unknown>) => Promise<{ error: unknown }>;
    upsert: (data: Record<string, unknown>, options?: { onConflict: string }) => Promise<{ error: unknown }>;
    update: (data: Record<string, unknown>) => { eq: (column: string, value: unknown) => Promise<{ error: unknown }> };
    delete: () => { eq: (column: string, value: unknown) => Promise<{ error: unknown }> };
  };
};

function isLikelyNetworkError(error: unknown): boolean {
  const message = String((error as { message?: string })?.message || error).toLowerCase();
  return (
    !navigator.onLine ||
    message.includes('failed to fetch') ||
    message.includes('networkerror') ||
    message.includes('network request failed') ||
    message.includes('load failed')
  );
}

class OfflineQueue {
  private isOnline: boolean = navigator.onLine;
  private isSyncing: boolean = false;
  private listeners: Set<(status: SyncStatus) => void> = new Set();
  private queueLengthCache: number = 0;

  constructor() {
    this.migrateLegacyQueue();
    this.setupListeners();
    this.updateQueueLengthCache();
  }

  /**
   * Migrate any existing localStorage queue to IndexedDB (one-time migration)
   */
  private async migrateLegacyQueue(): Promise<void> {
    try {
      const stored = localStorage.getItem(LEGACY_QUEUE_KEY);
      if (!stored) return;

      const legacyQueue = JSON.parse(stored) as QueuedAction[];
      if (legacyQueue.length > 0) {
        logger.log('[OfflineQueue] Migrating', legacyQueue.length, 'items from localStorage to IndexedDB');
        for (const action of legacyQueue) {
          await offlineQueueStore.add(action);
        }
      }
      
      // Remove legacy queue after migration
      localStorage.removeItem(LEGACY_QUEUE_KEY);
      logger.log('[OfflineQueue] Legacy queue migration complete');
      this.updateQueueLengthCache();
    } catch (error) {
      logger.error('[OfflineQueue] Failed to migrate legacy queue:', error);
    }
  }

  private async updateQueueLengthCache(): Promise<void> {
    this.queueLengthCache = await offlineQueueStore.count();
  }

  private setupListeners(): void {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.notifyListeners('online');
      this.syncQueue();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      this.notifyListeners('offline');
    });

    // Try to sync periodically when online (safety net)
    setInterval(() => {
      if (this.queueLengthCache > 0 && !this.isSyncing) {
        this.syncQueue();
      }
    }, 5000); // Every 5 seconds

    // Re-check on tab focus / visibility — 'online' event can be delayed
    const recheck = () => {
      if (this.queueLengthCache > 0 && !this.isSyncing) {
        this.syncQueue();
      }
    };
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') recheck();
    });
  }

  private notifyListeners(status: SyncStatus): void {
    this.listeners.forEach(listener => listener(status));
  }

  public subscribe(listener: (status: SyncStatus) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  public getQueueLength(): number {
    return this.queueLengthCache;
  }

  public async getQueueLengthAsync(): Promise<number> {
    await this.updateQueueLengthCache();
    return this.queueLengthCache;
  }

  public isNetworkOnline(): boolean {
    return this.isOnline;
  }

  public async addToQueue(
    table: string,
    action: 'insert' | 'update' | 'delete' | 'upsert',
    data: Record<string, unknown>,
    options?: { onConflict?: string }
  ): Promise<string> {
    const queuedAction: QueuedAction = {
      id: crypto.randomUUID(),
      table,
      action,
      data,
      timestamp: Date.now(),
      retryCount: 0,
      onConflict: options?.onConflict,
    };

    await offlineQueueStore.add(queuedAction);
    await this.updateQueueLengthCache();
    this.notifyListeners(this.isOnline ? 'online' : 'offline');

    // Try to sync immediately if online
    if (navigator.onLine) {
      this.syncQueue();
    }

    return queuedAction.id;
  }

  public async syncQueue(): Promise<void> {
    const currentLength = await offlineQueueStore.count();
    const browserOnline = navigator.onLine;
    if (this.isOnline !== browserOnline) {
      this.isOnline = browserOnline;
      this.notifyListeners(browserOnline ? 'online' : 'offline');
    }

    if (this.isSyncing || currentLength === 0 || !browserOnline) {
      return;
    }

    this.isSyncing = true;
    this.notifyListeners('syncing');

    const queue = await offlineQueueStore.getAll();
    let abortedForNetwork = false;

    for (const action of queue) {
      try {
        await this.executeAction(action);
        await offlineQueueStore.remove(action.id);
      } catch (error) {
        logger.error(`Failed to sync action ${action.id}:`, error);
        if (isLikelyNetworkError(error)) {
          this.isOnline = false;
          abortedForNetwork = true;
          this.notifyListeners('offline');
          break;
        }
        
        if (action.retryCount < MAX_RETRIES) {
          action.retryCount++;
          await offlineQueueStore.update(action);
        } else {
          logger.error(`Action ${action.id} exceeded max retries, discarding`);
          await offlineQueueStore.remove(action.id);
        }
      }
    }

    await this.updateQueueLengthCache();
    this.isSyncing = false;
    this.notifyListeners(abortedForNetwork ? 'offline' : 'complete');
  }

  private async executeAction(action: QueuedAction): Promise<void> {
    const { table, action: actionType, data, onConflict } = action;
    const client = supabase as unknown as DynamicTableClient;

    switch (actionType) {
      case 'insert': {
        const { error: insertError } = await client.from(table).insert(data);
        if (insertError) throw insertError;
        break;
      }

      case 'upsert': {
        const { error: upsertError } = await client
          .from(table)
          .upsert(data, onConflict ? { onConflict } : undefined);
        if (upsertError) throw upsertError;
        break;
      }

      case 'update': {
        const { id, ...updateData } = data;
        const { error: updateError } = await client.from(table).update(updateData).eq('id', id);
        if (updateError) throw updateError;
        break;
      }

      case 'delete': {
        const { error: deleteError } = await client.from(table).delete().eq('id', data.id);
        if (deleteError) throw deleteError;
        break;
      }
    }
  }

  public async clearQueue(): Promise<void> {
    await offlineQueueStore.clear();
    await this.updateQueueLengthCache();
  }
}

export const offlineQueue = new OfflineQueue();
